const OPENVERSE_API_BASE = 'https://api.openverse.org/v1';

export interface OpenverseImage {
  id: string;
  title: string | null;
  url: string;
  thumbnail: string;
  creator: string | null;
  creator_url: string | null;
  license: string;
  license_version: string | null;
  license_url: string | null;
  foreign_landing_url: string | null;
  attribution: string | null;
  height: number | null;
  width: number | null;
  category: string | null;
  source: string | null;
}

interface OpenverseSearchResponse {
  result_count?: number;
  page_count?: number;
  page_size?: number;
  page?: number;
  results?: unknown;
}

export async function searchOpenverseImages(
  query: string,
  page = 1,
  pageSize = 20
): Promise<{ images: OpenverseImage[]; totalPages: number }> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return { images: [], totalPages: 0 };
  const normalizedPage = Number.isFinite(page) ? Math.max(1, Math.floor(page)) : 1;
  const normalizedPageSize = Number.isFinite(pageSize)
    ? Math.min(20, Math.max(1, Math.floor(pageSize)))
    : 20;

  const url = new URL('/v1/images/', OPENVERSE_API_BASE);
  url.searchParams.set('q', trimmedQuery);
  url.searchParams.set('page', String(normalizedPage));
  url.searchParams.set('page_size', String(normalizedPageSize));
  url.searchParams.set('mature', 'false');
  url.searchParams.set('filter_dead', 'true');

  const response = await fetch(url.toString());

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('Openverse rate limit reached. Please wait a moment and try again.');
    }
    const body = await response.text().catch(() => '');
    throw new Error(`Openverse search failed (${response.status}). ${body.slice(0, 200)}`);
  }

  const data = (await response.json()) as OpenverseSearchResponse;

  if (!Array.isArray(data.results)) {
    return { images: [], totalPages: 0 };
  }

  const images = data.results
    .map((item): OpenverseImage | null => {
      const record = item as Record<string, unknown>;
      const id = typeof record.id === 'string' ? record.id : '';
      const imageUrl = typeof record.url === 'string' ? record.url : '';
      const thumbnail = typeof record.thumbnail === 'string' ? record.thumbnail : '';
      if (!id || (!imageUrl && !thumbnail)) return null;

      return {
        id,
        title: readString(record.title),
        url: imageUrl,
        thumbnail,
        creator: readString(record.creator),
        creator_url: readString(record.creator_url),
        license: typeof record.license === 'string' ? record.license : 'unknown',
        license_version: readString(record.license_version),
        license_url: readString(record.license_url),
        foreign_landing_url: readString(record.foreign_landing_url),
        attribution: readString(record.attribution),
        height: typeof record.height === 'number' ? record.height : null,
        width: typeof record.width === 'number' ? record.width : null,
        category: readString(record.category),
        source: readString(record.source),
      };
    })
    .filter((img): img is OpenverseImage => Boolean(img));

  return { images, totalPages: data.page_count ?? 0 };
}

export function getOpenverseImageUrl(image: OpenverseImage): string {
  return image.url || image.thumbnail;
}

export function getOpenverseThumbnailUrl(image: OpenverseImage): string {
  return image.thumbnail || image.url;
}

export function generateOpenverseAltText(image: OpenverseImage, searchTerm?: string): string {
  const title = image.title?.trim();
  if (title) return title;
  const fallback = searchTerm?.trim();
  return fallback ? `Image of ${fallback}` : 'Image from Openverse';
}

export function buildOpenverseCredit(image: OpenverseImage): string {
  const creator = image.creator?.trim() || 'Unknown creator';
  const license = image.license?.toUpperCase() || 'CC';
  const version = image.license_version ? ` ${image.license_version}` : '';
  return `"${image.title?.trim() || 'Untitled'}" by ${creator} (${license}${version}) via Openverse`;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}
